'use client';

import type { Sport } from '@sharpr/types';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { fetchError } from '@/lib/fetchError';
import type { SelectionViewResponse } from '@/types/selection';
import type { AccessTier } from '@/lib/access';
import { useNbaBoard } from './useNbaBoard';

// MLB's odds payload, by the viewer's tier for the sport: the authed per-viewer endpoint (no-store) or the public
// edge-cached one. The props screen and the slate share this query, so both read one cache entry. The tier in the key
// refetches the right endpoint when access changes; it stays under the ['players'] prefix the version pollers invalidate.
export const oddsPlayersQuery = (sport: Sport, tier: AccessTier) => queryOptions({
  queryKey: ['players', 'odds', sport, tier] as const,
  queryFn: async (): Promise<SelectionViewResponse> => {
    const response = await fetch(`${tier === 'public' ? '/api/odds/public/players' : '/api/odds/players'}?sport=${sport}`);
    if (!response.ok) throw await fetchError(response);
    return response.json() as Promise<SelectionViewResponse>;
  },
});

const NO_QUESTIONABLE: ReturnType<typeof useNbaBoard>['questionable'] = [];

// The odds screen's data, by sport. NBA: the seed, kept live over board:nba and priced in the browser (spec §4.4).
// MLB: today's server-priced payload, until phase 2 moves it onto the seed.
export function useOddsBoard({ sport, ...options }: { sport: Sport } & Parameters<typeof useNbaBoard>[0]) {
  const { enabled, tier } = options;
  const nba = useNbaBoard({ ...options, enabled: enabled && sport === 'nba' });
  const mlb = useQuery({
    ...oddsPlayersQuery(sport, tier),
    // Backstop only — version poll drives immediate refetch; this catches a failed poll.
    refetchInterval: 60000,
    refetchOnWindowFocus: true,
    enabled: enabled && sport === 'mlb',
    // Survive the rare claim->live access correction (index 3) without blanking;
    // still blank on a sport switch (index 2), where prior-sport rows would be wrong.
    placeholderData: (prev, prevQuery) =>
      prevQuery && prevQuery.queryKey[2] === sport ? prev : undefined,
    // retry / retryDelay inherit from QueryProvider (skip 401/429; else backoff w/ Retry-After).
  });
  return sport === 'nba' ? nba : { data: mlb.data, error: mlb.error, refetch: mlb.refetch, questionable: NO_QUESTIONABLE };
}
